import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, TextField, Select, Stack, QuestionCircleFilledIcon, Tooltip } from '@eds-web/react';
import { FormikProps } from 'formik';
import styled from '@emotion/styled';

import { PayoutFormValues } from '../../types/payout.types';
const BankImg = require('../../../../public/assets/CheckImage.png').default;

interface BankAccountProps {
    formik: FormikProps<PayoutFormValues>;
    disabled: boolean;
    handleChangeFormValues: (_: string, evt: any) => void;
}

const BankInformation = styled.div`
    display: inline-block;
    width: 260px;
    height: 156px;
    background: url(${BankImg}) 50% no-repeat;
    background-size: contain;
`;

export const BankAccount: React.FC<BankAccountProps> = ({
    formik,
    disabled,
    handleChangeFormValues
}) => {
    const { t } = useTranslation();

    const foreignBankOptions = [
        { value: 'true', label: t('epic.esport.label.payoutInfo.foreignBank.yes') },
        { value: 'false', label: t('epic.esport.label.payoutInfo.foreignBank.no') }
    ];

    const handleForeignBankChange = (selected: any) => {
        formik.setFieldValue('foreignBank', selected?.value === 'true');
        formik.setFieldTouched('foreignBank', true, false);
    };

    const currentForeignBankValue = foreignBankOptions.find(
        (option) => option.value === formik.values.foreignBank?.toString()
    );

    return (
        <Stack gap={16}>
            <Text variant="headingSM">{t('epic.esport.label.bank_account')}</Text>

            {/* Foreign Bank Selection */}
            <Select
                label={t('epic.esport.label.payoutInfo.foreignBank')}
                name="foreignBank"
                value={currentForeignBankValue}
                onChange={handleForeignBankChange}
                options={foreignBankOptions}
                disabled={disabled}
                error={formik.touched.foreignBank && formik.errors.foreignBank}
            />

            {/* Check Name */}
            <TextField
                label={t('epic.esport.label.payoutInfo.checkName')}
                name="checkName"
                value={formik.values.checkName}
                onChange={handleChangeFormValues}
                placeholder={t('epic.esport.label.payoutInfo.checkName')}
                disabled={disabled}
                error={formik.touched.checkName && formik.errors.checkName}
                required
            />

            {/* Bank Name */}
            <TextField
                label={t('epic.esport.label.payoutInfo.bankName')}
                name="bankName"
                value={formik.values.bankName}
                onChange={handleChangeFormValues}
                placeholder={t('epic.esport.label.payoutInfo.bankName')}
                disabled={disabled}
                error={formik.touched.bankName && formik.errors.bankName}
                required
            />

            <TextField
                label={t('epic.esport.label.payoutInfo.routingNo')}
                name="routingNo"
                value={formik.values.routingNo}
                onChange={handleChangeFormValues}
                placeholder={t('epic.esport.label.payoutInfo.routingNo')}
                disabled={disabled}
                error={formik.touched.routingNo && formik.errors.routingNo}
                required={!formik.values.foreignBank}
                endAdornment={
                    <Tooltip label={<BankInformation />}>
                        <QuestionCircleFilledIcon
                            style={{ pointerEvents: 'auto' }}
                            color={'primary'}
                        />
                    </Tooltip>
                }
            />

            {/* Bank Account Number */}
            <TextField
                label={t('epic.esport.label.payoutInfo.bankAccountNo')}
                name="bankAccountNo"
                value={formik.values.bankAccountNo}
                onChange={handleChangeFormValues}
                placeholder={
                    formik.values.foreignBank
                        ? t('epic.esport.label.payoutInfo.IBAN')
                        : t('epic.esport.label.payoutInfo.bankAccountNo')
                }
                disabled={disabled}
                error={formik.touched.bankAccountNo && formik.errors.bankAccountNo}
                required
                endAdornment={
                    <Tooltip label={<BankInformation />}>
                        <QuestionCircleFilledIcon
                            style={{ pointerEvents: 'auto' }}
                            color={'primary'}
                        />
                    </Tooltip>
                }
            />

            {formik.values.foreignBank && (
                <TextField
                    label={t('epic.esport.label.payoutInfo.swiftCode')}
                    name="swiftCode"
                    value={formik.values.swiftCode}
                    onChange={handleChangeFormValues}
                    placeholder={t('epic.esport.label.payoutInfo.swiftCode')}
                    disabled={disabled}
                    error={formik.touched.swiftCode && formik.errors.swiftCode}
                    required
                />
            )}

            <TextField
                label={t('epic.esport.label.payoutInfo.additionalAccountDetail')}
                name="additionalAccountDetail"
                value={formik.values.additionalAccountDetail}
                onChange={handleChangeFormValues}
                placeholder={t('epic.esport.label.payoutInfo.additionalAccountDetail')}
                disabled={disabled}
                error={
                    formik.touched.additionalAccountDetail && formik.errors.additionalAccountDetail
                }
                multiline
            />
        </Stack>
    );
};
